import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { ChevronDown } from "lucide-react";
import { resetCookieConsent } from '@/components/CookieConsent';
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { stripLangPrefix } from '@/components/LangWrapper';
import '@/pages/landing-animations.css';
import { useHostBranding } from '@/components/HostBranding';

// Locale switcher data — added 2026-05-24 after multi-region SEO diagnosis
// revealed Google indexed 1 of ~4,200 /pl URLs and 0 of /de. Root cause:
// hreflang HEAD tags exist but the EN homepage had ZERO <a> anchors to any
// locale (53 anchors, 0 pointing to /pl, /de, /es, /it, /fr…). Without
// PR-flow from the root, all 22 locale subtrees were discovery islands.
// This block emits one <a href> per locale in SSR HTML so Google can crawl
// + weight every locale homepage from every page. Native script names so
// the anchor text is also a localized signal (Polski vs Polish, etc.).
// Alphabetical by locale code (canonical order across all language lists).
const LOCALES: { code: string; name: string }[] = [
  { code: 'ar', name: 'العربية' },
  { code: 'cs', name: 'Čeština' },
  { code: 'de', name: 'Deutsch' },
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fil', name: 'Filipino' },
  { code: 'fr', name: 'Français' },
  { code: 'hi', name: 'हिन्दी' },
  { code: 'hu', name: 'Magyar' },
  { code: 'id', name: 'Bahasa Indonesia' },
  { code: 'it', name: 'Italiano' },
  { code: 'ja', name: '日本語' },
  { code: 'ko', name: '한국어' },
  { code: 'lt', name: 'Lietuvių' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'pl', name: 'Polski' },
  { code: 'pt-br', name: 'Português (BR)' },
  { code: 'pt-pt', name: 'Português (PT)' },
  { code: 'ro', name: 'Română' },
  { code: 'th', name: 'ไทย' },
  { code: 'tr', name: 'Türkçe' },
  { code: 'uk', name: 'Українська' },
  { code: 'vi', name: 'Tiếng Việt' },
];

interface FooterColumn {
  title: string;
  links: { to: string; label: string; external?: boolean }[];
}

export const MarketingFooter = () => {
  const { t } = useTranslation();
  const brand = useHostBranding();
  const [openCol, setOpenCol] = useState<number | null>(null);
  const lp = useLocalizedPath();
  const location = useLocation();

  // basePath = current pathname stripped of language prefix + trailing slash.
  // Used so the locale switcher links to the SAME page in another locale, not
  // always back to that locale's home. Matches HreflangTags.tsx logic so
  // <link rel="alternate"> and visible <a> point to identical URLs (Google
  // consistency signal).
  const basePath = stripLangPrefix(location.pathname).replace(/\/+$/, '');
  const localeHref = (code: string): string =>
    code === 'en' ? (basePath || '/') : `/${code}${basePath}`;

  // Hide locale switcher on protected/non-translatable routes to avoid
  // pointing Google at 404s. Matches HreflangTags skipPrefixes exactly.
  const skipLocaleSwitcher = ['/auth', '/dashboard', '/cctv-design', '/checkout-success', '/api-docs', '/camera/', '/compare/sku/']
    .some(p => basePath === p || basePath.startsWith(p.endsWith('/') ? p : p + '/'));

  const columns: FooterColumn[] = [
    {
      title: t('footer_product', 'Product'),
      links: [
        { to: lp("/pricing"), label: t('footer_pricing', 'Pricing') },
        { to: lp("/calculators"), label: t('footer_calculators', 'Calculators') },
        { to: lp("/calculator/fov"), label: t('footer_fov_calculator', 'FOV Calculator') },
        { to: lp("/calculator/dori"), label: t('footer_dori_calculator', 'DORI Calculator') },
        { to: lp("/lens-calculator"), label: t('footer_lens_calculator', 'Lens Calculator') },
        { to: lp("/storage-calculator"), label: t('footer_storage_calculator', 'Storage Calculator') },
        { to: lp("/bandwidth-calculator"), label: t('footer_bandwidth_calculator_landing', 'Bandwidth Calculator') },
        { to: lp("/calculator/poe"), label: t('footer_poe_calculator', 'PoE Budget Calculator') },
        { to: lp("/cameras"), label: t('footer_camera_catalog', 'Camera Catalog') },
      ],
    },
    {
      title: t('footer_resources', 'Resources'),
      links: [
        { to: lp("/docs"), label: t('footer_docs', 'Documentation') },
        { to: lp("/glossary"), label: t('footer_glossary', 'Glossary') },
        { to: lp("/camera-physics"), label: t('footer_camera_physics', 'Camera Physics') },
        { to: lp("/en-62676-4-calculator"), label: t('footer_en_62676', 'EN 62676-4 Standard') },
        { to: lp("/johnson-criteria-calculator"), label: t('footer_johnson', 'Johnson Criteria') },
        { to: lp("/ndaa-cctv-compliance"), label: t('footer_ndaa', 'NDAA Section 889') },
      ],
    },
    {
      title: t('footer_legal', 'Legal & Other'),
      links: [
        { to: lp("/changelog"), label: t('footer_changelog', 'Changelog') },
        { to: "/attributions", label: t('footer_attributions', 'Attributions') },
        { to: lp("/rodo"), label: t('footer_privacy_policy', 'Privacy Policy') },
        { to: lp("/feedback"), label: t('footer_feedback', 'Send Feedback') },
        { to: lp("/lens/2.8"), label: t('footer_lens_fov_guide', 'Lens FOV Guide') },
      ],
    },
  ];

  const toggleCol = (idx: number) => {
    setOpenCol(openCol === idx ? null : idx);
  };

  return (
    <footer className="pelco-footer">
      <div className="pelco-container" style={{ paddingTop: '3rem', paddingBottom: '2rem' }}>
        {/* Nav columns */}
        <div className="pelco-footer__nav">
          {columns.map((col, idx) => (
            <div key={idx} className="pelco-footer__col">
              {/* Desktop heading */}
              <span className="pelco-footer__heading hidden md:block">
                {col.title}
              </span>
              {/* Mobile accordion heading */}
              <span
                className="pelco-footer__heading md:hidden"
                onClick={() => toggleCol(idx)}
                aria-expanded={openCol === idx}
                role="button"
              >
                {col.title}
                <ChevronDown className="chevron w-4 h-4" />
              </span>
              {/* Links */}
              <nav className={`md:block ${openCol === idx ? 'pelco-footer__links--expanded' : 'pelco-footer__links--collapsed'}`}>
                {col.links.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="pelco-footer__link"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>
          ))}
        </div>

        {/* Locale switcher — SSR-visible <a> anchors so Google can crawl all
            22 locale homepages from any page. Added 2026-05-24 to fix the
            zero-rank in non-EN markets per multi-region diagnostic.
            Uses plain <a> rather than <Link> so the href is honest static
            HTML (no client-side interception), matching how hreflang HEAD
            tags resolve to URLs. */}
        {!skipLocaleSwitcher && (
          <nav
            aria-label={t('footer_locale_nav_label', 'Choose language')}
            className="mt-8 pt-6 border-t"
            style={{ borderColor: 'var(--color-gray-border)' }}
          >
            <h3 className="text-[11px] uppercase tracking-[0.16em] font-mono text-neutral-500 dark:text-brand-text-secondary text-center mb-4">
              {t('footer_locale_heading', 'Language')}
            </h3>
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-[12px]">
              {LOCALES.map((loc) => (
                <a
                  key={loc.code}
                  href={localeHref(loc.code)}
                  hrefLang={loc.code === 'pt-br' ? 'pt-BR' : loc.code === 'pt-pt' ? 'pt-PT' : loc.code}
                  className="pelco-footer__link"
                  style={{ padding: 0 }}
                >
                  {loc.name}
                </a>
              ))}
            </div>
          </nav>
        )}

        {/* EU signature strip */}
        <div
          className="mt-8 pt-6 border-t text-[11px] leading-relaxed text-neutral-500 dark:text-brand-text-secondary text-center"
          style={{ borderColor: 'var(--color-gray-border)', fontFamily: 'var(--font-mono)' }}
        >
          <p className="tracking-[0.08em]">
            {t('common:eu_footer_tagline', 'Designed, built, and hosted entirely in the European Union. Your data never leaves the EU.')}
          </p>
        </div>

        {/* Bottom bar */}
        <div className="pelco-footer__bottom flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
          <span>{brand ? (brand.footerText || `© 2026 ${brand.name}`) : <>&copy; 2026 TodAidesign &mdash; by TodAidesign &middot; {t('common:eu_footer_signature', 'Made & hosted in EU')}</>}</span>
          <div className="flex items-center gap-4">
            <button
              onClick={resetCookieConsent}
              className="pelco-footer__link"
              style={{ padding: 0, background: 'none', border: 'none', cursor: 'pointer' }}
            >
              {t('footer_cookie_settings', 'Cookie Settings')}
            </button>
            <Link to={lp("/rodo")} className="pelco-footer__link" style={{ padding: 0 }}>
              {t('footer_privacy_policy', 'Privacy Policy')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default MarketingFooter;
